/**
 * Same-origin installer redirects. The backend chooses stable or staging from
 * its deployment identity, so each href is channel-correct on the first paint and
 * works without a browser-side GitHub lookup.
 *
 * Paths carry the platform as well as the arch: there is no arm64 Windows build
 * and no x86_64 macOS one, so the backend answers per platform rather than from
 * one global arch list.
 */
export const DESKTOP_DMG_AARCH64 = "/api/desktop/downloads/darwin/aarch64";
export const DESKTOP_DMG_X86_64 = "/api/desktop/downloads/darwin/x86_64";
export const DESKTOP_WINDOWS_X86_64 = "/api/desktop/downloads/windows/x86_64";

/** False while YAG-442 keeps the Intel lane out of the release workflow. */
export const DESKTOP_INTEL_AVAILABLE = false;

/**
 * The CLI installer per shell family. The curl line has no Windows
 * equivalent: PowerShell aliases `curl` to Invoke-WebRequest and has no `sh`,
 * so the same command there fails with "'sh' is not recognized". Windows
 * gets the PowerShell installer, fetched and run in memory the way winget,
 * Scoop, and Bun do it.
 */
export type InstallShell = "posix" | "powershell";

export const CLI_INSTALL_COMMANDS: Record<InstallShell, string> = {
  posix: "curl -fsSL https://yagni.app/install.sh | sh",
  powershell: "irm https://yagni.app/install.ps1 | iex",
};

export const INSTALL_SHELL_LABELS: Record<InstallShell, string> = {
  posix: "macOS / Linux",
  powershell: "Windows (PowerShell)",
};

/**
 * Which installer to lead with. Only the OS family matters here, and the
 * user-agent string is reliable for that much (unlike CPU architecture, which
 * is why the desktop buttons never guess).
 */
export function detectInstallShell(nav: Pick<Navigator, "userAgent"> | undefined = globalThis.navigator): InstallShell {
  return nav && /\bWindows\b/i.test(nav.userAgent) ? "powershell" : "posix";
}

export interface DmgLinks {
  aarch64: string;
  /** Null while no Intel build is published — callers must not render a link. */
  x86_64: string | null;
  /** The x64 NSIS installer; Windows publishes no arm64 build. */
  windows: string;
}

export function useLatestDmgLinks(): DmgLinks {
  return {
    aarch64: DESKTOP_DMG_AARCH64,
    x86_64: DESKTOP_INTEL_AVAILABLE ? DESKTOP_DMG_X86_64 : null,
    windows: DESKTOP_WINDOWS_X86_64,
  };
}
